import React, { useState, useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useAppSettings } from '../hooks/useAppSettings'
import { formatMessageTimestamp } from '../utils/time'
import { db } from '../utils/firebase'
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  getDoc,
  serverTimestamp,
  deleteField
} from 'firebase/firestore'
import {
  Search,
  Send,
  ArrowLeft,
  MessageCircle,
  Users,
  Check,
  X,
  Plus,
  MoreHorizontal,
  Archive,
  ArchiveRestore,
  Mail,
  MailOpen
} from 'lucide-react'
import './MessagesPage.css'

function MessagesPage() {
  const { userProfile } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [conversations, setConversations] = useState([])
  const [selectedConversation, setSelectedConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [neighbors, setNeighbors] = useState([])
  const [showNewChat, setShowNewChat] = useState(false)
  const [loading, setLoading] = useState(true)
  const [startWithProcessed, setStartWithProcessed] = useState(false)
  const [selectedNeighborIds, setSelectedNeighborIds] = useState(new Set())
  const [groupName, setGroupName] = useState('')
  const [creatingGroup, setCreatingGroup] = useState(false)
  const [showGroupInfo, setShowGroupInfo] = useState(false)
  const [groupInfoNameDraft, setGroupInfoNameDraft] = useState('')
  const [savingGroupName, setSavingGroupName] = useState(false)
  const [activeTab, setActiveTab] = useState('inbox') // 'inbox' | 'archived'
  const [menuOpenFor, setMenuOpenFor] = useState(null)

  // Close the row menu on any outside click.
  useEffect(() => {
    if (!menuOpenFor) return
    const close = () => setMenuOpenFor(null)
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [menuOpenFor])

  const canSelectWholeNeighborhood = !!(
    userProfile?.isAdmin || userProfile?.isBoardMember || userProfile?.isSuperAdmin
  )
  const messagesEndRef = useRef(null)

  // Load conversations
  useEffect(() => {
    if (!userProfile?.uid) return
    const q = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', userProfile.uid),
      orderBy('lastMessageAt', 'desc')
    )
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const convos = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      setConversations(convos)
      setLoading(false)
    }, (error) => { console.error("Error loading conversations:", error); setLoading(false) })
    return () => unsubscribe()
  }, [userProfile?.uid])

  // Handle startWith URL parameter (from directory message icon)
  useEffect(() => {
    const startWithUserId = searchParams.get('startWith')
    if (!startWithUserId || !userProfile?.uid || loading || startWithProcessed) return

    const handleStartWith = async () => {
      const existingConvo = conversations.find(c => c.participants.includes(startWithUserId))
      if (existingConvo) {
        setSelectedConversation(existingConvo)
        setSearchParams({})
        setStartWithProcessed(true)
        return
      }
      try {
        const userDoc = await getDoc(doc(db, 'users', startWithUserId))
        if (!userDoc.exists()) {
          setSearchParams({}); setStartWithProcessed(true); return
        }
        const neighborData = { id: userDoc.id, ...userDoc.data() }
        const newConvo = await addDoc(collection(db, 'conversations'), {
          participants: [userProfile.uid, neighborData.id],
          participantNames: {
            [userProfile.uid]: userProfile.name,
            [neighborData.id]: neighborData.name
          },
          participantPhotos: {
            [userProfile.uid]: userProfile.profilePhotoURL || null,
            [neighborData.id]: neighborData.profilePhotoURL || null
          },
          neighborhoodId: userProfile.neighborhoodId,
          createdAt: serverTimestamp(),
          lastMessageAt: serverTimestamp(),
          lastMessage: '',
          unreadCount: { [userProfile.uid]: 0, [neighborData.id]: 0 }
        })
        setSelectedConversation({
          id: newConvo.id,
          participants: [userProfile.uid, neighborData.id],
          participantNames: {
            [userProfile.uid]: userProfile.name,
            [neighborData.id]: neighborData.name
          },
          participantPhotos: {
            [userProfile.uid]: userProfile.profilePhotoURL || null,
            [neighborData.id]: neighborData.profilePhotoURL || null
          }
        })
        setSearchParams({}); setStartWithProcessed(true)
      } catch (error) {
        console.error('Error starting conversation:', error)
        setSearchParams({}); setStartWithProcessed(true)
      }
    }
    handleStartWith()
  }, [searchParams, userProfile, conversations, loading, startWithProcessed, setSearchParams])

  // Load messages for selected conversation
  useEffect(() => {
    if (!selectedConversation) return
    const q = query(
      collection(db, 'conversations', selectedConversation.id, 'messages'),
      orderBy('createdAt', 'asc')
    )
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      setMessages(msgs)
      markMessagesAsRead(selectedConversation.id)
    })
    return () => unsubscribe()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedConversation?.id])

  useEffect(() => {
    // Scroll ONLY the message list, not the whole page. The previous
    // `scrollIntoView({ behavior: 'smooth' })` call bubbled up to the
    // window, which pushed the conversation header off the top of the
    // viewport whenever a thread was opened — that's the "anchors too
    // far down" symptom. `block: 'nearest'` constrains the scroll to
    // the nearest scrollable ancestor (.conv-messages).
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [messages])

  // Load neighbors — used both for the New Chat picker and for
  // enriching the right-side context card with a DM partner's address.
  // Load once when neighborhood is known, not gated on showNewChat.
  useEffect(() => {
    if (!userProfile?.neighborhoodId) return
    const loadNeighbors = async () => {
      const q = query(
        collection(db, 'users'),
        where('neighborhoodId', '==', userProfile.neighborhoodId)
      )
      const snapshot = await getDocs(q)
      const neighborsList = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(n => n.id !== userProfile.uid)
      setNeighbors(neighborsList)
    }
    loadNeighbors()
  }, [userProfile?.neighborhoodId])

  const markMessagesAsRead = async (conversationId) => {
    try {
      await updateDoc(doc(db, 'conversations', conversationId), {
        [`unreadCount.${userProfile.uid}`]: 0,
        [`lastReadAt.${userProfile.uid}`]: serverTimestamp()
      })
    } catch (error) {
      console.error('Error marking messages as read:', error)
    }
  }

  const setArchived = async (convo, archive) => {
    try {
      await updateDoc(doc(db, 'conversations', convo.id), {
        [`archivedBy.${userProfile.uid}`]: archive ? true : deleteField(),
      })
      // If we just archived the open convo, close it so the user isn't
      // stranded on a thread that no longer shows in the current tab.
      if (archive && selectedConversation?.id === convo.id) {
        setSelectedConversation(null)
      }
    } catch (err) {
      console.error('Error updating archive state:', err)
    }
  }

  const markAsUnread = async (convo) => {
    try {
      const current = convo.unreadCount?.[userProfile.uid] || 0
      await updateDoc(doc(db, 'conversations', convo.id), {
        [`unreadCount.${userProfile.uid}`]: current > 0 ? current : 1,
        [`lastReadAt.${userProfile.uid}`]: deleteField(),
      })
    } catch (err) {
      console.error('Error marking as unread:', err)
    }
  }

  const markAsRead = async (convo) => {
    try {
      await updateDoc(doc(db, 'conversations', convo.id), {
        [`unreadCount.${userProfile.uid}`]: 0,
        [`lastReadAt.${userProfile.uid}`]: serverTimestamp(),
      })
    } catch (err) {
      console.error('Error marking as read:', err)
    }
  }

  const sendMessage = async (e) => {
    e.preventDefault()
    if (!newMessage.trim() || !selectedConversation) return
    const messageText = newMessage.trim()
    setNewMessage('')
    try {
      await addDoc(collection(db, 'conversations', selectedConversation.id, 'messages'), {
        text: messageText,
        senderId: userProfile.uid,
        senderName: userProfile.name,
        createdAt: serverTimestamp()
      })
      const otherUserIds = selectedConversation.participants.filter(p => p !== userProfile.uid)
      const updates = {
        lastMessage: messageText,
        lastMessageAt: serverTimestamp(),
        lastSenderId: userProfile.uid,
        [`lastReadAt.${userProfile.uid}`]: serverTimestamp(),
      }
      otherUserIds.forEach(uid => {
        updates[`unreadCount.${uid}`] =
          (selectedConversation.unreadCount?.[uid] || 0) + 1
      })
      await updateDoc(doc(db, 'conversations', selectedConversation.id), updates)
    } catch (error) {
      console.error('Error sending message:', error)
    }
  }

  const startNewConversation = async (neighbor) => {
    const existingConvo = conversations.find(c =>
      !c.isGroup && c.participants.length === 2 && c.participants.includes(neighbor.id)
    )
    if (existingConvo) {
      setSelectedConversation(existingConvo)
      setShowNewChat(false)
      return
    }
    return createConversation([neighbor])
  }

  const createConversation = async (recipientNeighbors) => {
    if (!recipientNeighbors || recipientNeighbors.length === 0) return null
    const isGroup = recipientNeighbors.length > 1
    const participantIds = [userProfile.uid, ...recipientNeighbors.map(n => n.id)]
    const participantNames = { [userProfile.uid]: userProfile.name }
    const participantPhotos = { [userProfile.uid]: userProfile.profilePhotoURL || null }
    const unreadCount = { [userProfile.uid]: 0 }
    const lastReadAt = {}
    recipientNeighbors.forEach(n => {
      participantNames[n.id] = n.name
      participantPhotos[n.id] = n.profilePhotoURL || null
      unreadCount[n.id] = 0
    })
    const docData = {
      participants: participantIds,
      participantNames,
      participantPhotos,
      neighborhoodId: userProfile.neighborhoodId,
      createdAt: serverTimestamp(),
      lastMessageAt: serverTimestamp(),
      lastMessage: '',
      unreadCount,
      lastReadAt,
      isGroup,
      createdBy: userProfile.uid,
    }
    if (isGroup && groupName.trim()) docData.name = groupName.trim()
    try {
      const newConvo = await addDoc(collection(db, 'conversations'), docData)
      setSelectedConversation({ id: newConvo.id, ...docData })
      setShowNewChat(false)
      setSelectedNeighborIds(new Set())
      setGroupName('')
      return newConvo
    } catch (error) {
      console.error('Error creating conversation:', error)
      return null
    }
  }

  const toggleNeighborSelection = (neighborId) => {
    setSelectedNeighborIds(prev => {
      const next = new Set(prev)
      if (next.has(neighborId)) next.delete(neighborId)
      else next.add(neighborId)
      return next
    })
  }

  const selectAllNeighbors = () => {
    setSelectedNeighborIds(new Set(filteredNeighbors.map(n => n.id)))
  }

  const clearNeighborSelection = () => {
    setSelectedNeighborIds(new Set())
    setGroupName('')
  }

  const openGroupInfo = () => {
    if (!selectedConversation) return
    setGroupInfoNameDraft(selectedConversation.name || '')
    setShowGroupInfo(true)
  }

  const saveGroupName = async () => {
    if (!selectedConversation) return
    const trimmed = groupInfoNameDraft.trim()
    setSavingGroupName(true)
    try {
      await updateDoc(doc(db, 'conversations', selectedConversation.id), {
        name: trimmed || null,
      })
      setSelectedConversation(prev => prev ? { ...prev, name: trimmed || null } : prev)
      setShowGroupInfo(false)
    } catch (err) {
      console.error('Error renaming group:', err)
      alert(`Could not rename group: ${err?.message || 'unknown error'}`)
    } finally {
      setSavingGroupName(false)
    }
  }

  const handleStartChat = async () => {
    const selected = neighbors.filter(n => selectedNeighborIds.has(n.id))
    if (selected.length === 0) return
    setCreatingGroup(true)
    try {
      if (selected.length === 1) await startNewConversation(selected[0])
      else await createConversation(selected)
    } finally {
      setCreatingGroup(false)
    }
  }

  const getConversationDisplay = (conversation) => {
    if (!conversation) return { name: '', photo: null, isGroup: false, memberCount: 0, others: [] }
    const participants = conversation.participants || []
    const otherIds = participants.filter(p => p !== userProfile.uid)
    const isGroup = !!conversation.isGroup || participants.length > 2
    const memberCount = participants.length
    const others = otherIds.map(id => ({
      id,
      name: conversation.participantNames?.[id] || 'Unknown',
      photo: conversation.participantPhotos?.[id] || null,
    }))
    if (!isGroup) {
      const otherId = otherIds[0]
      return {
        name: conversation.participantNames?.[otherId] || 'Unknown',
        photo: conversation.participantPhotos?.[otherId] || null,
        isGroup: false,
        memberCount,
        otherUserId: otherId,
        others,
      }
    }
    const memberNames = others.map(o => o.name).filter(Boolean)
    let composedName
    if (conversation.name) composedName = conversation.name
    else if (memberNames.length <= 3) composedName = memberNames.join(', ')
    else composedName = `${memberNames.slice(0, 2).join(', ')} +${memberNames.length - 2}`
    return {
      name: composedName || 'Group chat',
      photo: null,
      isGroup: true,
      memberCount,
      others,
    }
  }

  const { settings: appSettings } = useAppSettings()
  const formatTime = (timestamp) => formatMessageTimestamp(timestamp, appSettings)

  const filteredNeighbors = neighbors.filter(n =>
    n.name?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const isArchived = (c) => !!(c.archivedBy && c.archivedBy[userProfile?.uid])
  const inboxConversations = conversations.filter(c => !isArchived(c))
  const archivedConversations = conversations.filter(c => isArchived(c))
  const visibleConversations = activeTab === 'archived' ? archivedConversations : inboxConversations

  const totalUnread = inboxConversations.reduce((acc, c) => {
    return acc + ((c.unreadCount?.[userProfile?.uid] || 0) > 0 ? 1 : 0)
  }, 0)

  // Render helpers ------------------------------------------------------------

  const renderAvatarStack = (display, size = '') => {
    const cls = `avatar${size ? ' ' + size : ''}`
    if (display.isGroup) {
      // Show up to 2 stacked avatars for group threads.
      const shown = display.others.slice(0, 2)
      return (
        <div className={`avatar-stack${size ? ' avatar-stack-' + size : ''}`}>
          {shown.map((o, i) => (
            <div key={o.id} className={cls} style={{ zIndex: shown.length - i }} aria-hidden="true">
              {o.photo
                ? <img src={o.photo} alt="" />
                : <span>{o.name?.charAt(0).toUpperCase()}</span>}
            </div>
          ))}
          {display.others.length < 2 && (
            <div className={cls} aria-hidden="true"><Users size={16} /></div>
          )}
        </div>
      )
    }
    return (
      <div className={cls} aria-hidden="true">
        {display.photo
          ? <img src={display.photo} alt="" />
          : <span>{display.name?.charAt(0).toUpperCase()}</span>}
      </div>
    )
  }

  const renderConversationList = () => (
    <>
      <div className="messages-tabs" role="tablist" data-walk="messages-tabs">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'inbox'}
          className={`messages-tab${activeTab === 'inbox' ? ' active' : ''}`}
          onClick={() => setActiveTab('inbox')}
        >
          Inbox
          {inboxConversations.length > 0 && (
            <span className="messages-tab-count">{inboxConversations.length}</span>
          )}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'archived'}
          className={`messages-tab${activeTab === 'archived' ? ' active' : ''}`}
          onClick={() => setActiveTab('archived')}
        >
          Archived
          {archivedConversations.length > 0 && (
            <span className="messages-tab-count">{archivedConversations.length}</span>
          )}
        </button>
      </div>
    <div className="messages-thread-list">
      {loading ? (
        <div className="messages-loading"><div className="spinner"></div></div>
      ) : visibleConversations.length === 0 ? (
        <div className="card messages-empty-inline">
          <MessageCircle size={36} />
          <p className="muted">
            {activeTab === 'archived' ? 'No archived conversations' : 'No messages yet'}
          </p>
          {activeTab === 'inbox' && (
            <button className="btn-primary" onClick={() => setShowNewChat(true)}>
              Start a conversation
            </button>
          )}
        </div>
      ) : (
        visibleConversations.map(convo => {
          const display = getConversationDisplay(convo)
          const fieldUnread = convo.unreadCount?.[userProfile.uid] || 0
          let timestampUnread = false
          if (convo.lastMessage && convo.lastSenderId !== userProfile.uid) {
            const lastReadAt = convo.lastReadAt?.[userProfile.uid]
            const lastMessageAt = convo.lastMessageAt
            if (!lastReadAt) timestampUnread = true
            else if (lastMessageAt && lastReadAt) {
              const msgTime = lastMessageAt.toMillis ? lastMessageAt.toMillis() : (lastMessageAt.seconds || 0) * 1000
              const readTime = lastReadAt.toMillis ? lastReadAt.toMillis() : (lastReadAt.seconds || 0) * 1000
              timestampUnread = msgTime > readTime
            }
          }
          const unread = fieldUnread > 0 ? fieldUnread : (timestampUnread ? 1 : 0)
          const isActive = selectedConversation?.id === convo.id
          const archived = isArchived(convo)
          const menuOpen = menuOpenFor === convo.id
          return (
            <div key={convo.id} className="thread-row-wrap">
              <button
                type="button"
                className={`card interactive thread-card${isActive ? ' active' : ''}${unread > 0 ? ' unread' : ''}`}
                onClick={() => setSelectedConversation(convo)}
              >
                {renderAvatarStack(display)}
                <div className="thread-body">
                  <div className="thread-top">
                    <span className="thread-name">
                      {display.name}
                      {display.isGroup && (
                        <span className="thread-member-count"> · {display.memberCount}</span>
                      )}
                    </span>
                    <span className="thread-time muted">{formatTime(convo.lastMessageAt)}</span>
                  </div>
                  <p className="thread-preview">{convo.lastMessage || 'No messages yet'}</p>
                </div>
                {unread > 0 && <span className="badge thread-unread">{unread}</span>}
              </button>
              <div className="thread-menu">
                <button
                  type="button"
                  className="thread-menu-trigger"
                  aria-label="Conversation options"
                  onClick={(e) => {
                    e.stopPropagation()
                    setMenuOpenFor(menuOpen ? null : convo.id)
                  }}
                >
                  <MoreHorizontal size={16} />
                </button>
                {menuOpen && (
                  <div className="thread-menu-dropdown" onClick={(e) => e.stopPropagation()}>
                    {unread > 0 ? (
                      <button
                        type="button"
                        className="thread-menu-item"
                        onClick={() => { markAsRead(convo); setMenuOpenFor(null) }}
                      >
                        <MailOpen size={16} /> Mark as read
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="thread-menu-item"
                        onClick={() => { markAsUnread(convo); setMenuOpenFor(null) }}
                      >
                        <Mail size={16} /> Mark as unread
                      </button>
                    )}
                    {archived ? (
                      <button
                        type="button"
                        className="thread-menu-item"
                        onClick={() => { setArchived(convo, false); setMenuOpenFor(null) }}
                      >
                        <ArchiveRestore size={16} /> Unarchive
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="thread-menu-item"
                        onClick={() => { setArchived(convo, true); setMenuOpenFor(null) }}
                      >
                        <Archive size={16} /> Archive
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )
        })
      )}
    </div>
    </>
  )

  const renderNewChatPanel = () => (
    <div className="form-card new-chat-card">
      <div className="new-chat-top">
        <button
          type="button"
          className="btn-ghost new-chat-back"
          onClick={() => { setShowNewChat(false); clearNeighborSelection() }}
        >
          <ArrowLeft size={18} />
        </button>
        <span className="new-chat-title">
          {selectedNeighborIds.size > 1 ? 'New group chat' : 'New message'}
        </span>
      </div>

      <div className="new-chat-search">
        <Search size={16} />
        <input
          type="text"
          placeholder="Search neighbors"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {selectedNeighborIds.size >= 2 && (
        <input
          type="text"
          className="new-chat-groupname"
          placeholder="Group name (optional)"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
          maxLength={60}
        />
      )}

      <div className="new-chat-actions">
        <span className="muted new-chat-count">
          {selectedNeighborIds.size === 0
            ? 'Select neighbors to message'
            : `${selectedNeighborIds.size} selected`}
        </span>
        {canSelectWholeNeighborhood && filteredNeighbors.length > 1 && (
          <button type="button" className="btn-ghost small" onClick={selectAllNeighbors} title="Message every neighbor">
            <Users size={14} /> Select all
          </button>
        )}
        {selectedNeighborIds.size > 0 && (
          <button type="button" className="btn-ghost small" onClick={clearNeighborSelection} title="Clear selection">
            <X size={14} />
          </button>
        )}
      </div>

      <div className="new-chat-neighbors">
        {filteredNeighbors.map(neighbor => {
          const isSelected = selectedNeighborIds.has(neighbor.id)
          return (
            <button
              key={neighbor.id}
              type="button"
              className={`neighbor-row${isSelected ? ' selected' : ''}`}
              onClick={() => toggleNeighborSelection(neighbor.id)}
            >
              <span className={`neighbor-check${isSelected ? ' checked' : ''}`}>
                {isSelected ? <Check size={14} /> : null}
              </span>
              <div className="avatar sm" aria-hidden="true">
                {neighbor.profilePhotoURL
                  ? <img src={neighbor.profilePhotoURL} alt="" />
                  : <span>{neighbor.name?.charAt(0).toUpperCase()}</span>}
              </div>
              <span className="neighbor-name">{neighbor.name}</span>
            </button>
          )
        })}
      </div>

      <div className="new-chat-footer">
        <button
          type="button"
          className="btn-primary new-chat-start"
          disabled={selectedNeighborIds.size === 0 || creatingGroup}
          onClick={handleStartChat}
        >
          {creatingGroup
            ? 'Starting…'
            : selectedNeighborIds.size <= 1
              ? 'Start chat'
              : `Start group chat (${selectedNeighborIds.size})`}
        </button>
      </div>
    </div>
  )

  const activeDisplay = selectedConversation ? getConversationDisplay(selectedConversation) : null

  return (
    <div className="detail-layout messages-page">
      {/* LEFT — thread list + header */}
      <aside className={`detail-list messages-list-col${selectedConversation ? ' hide-mobile' : ''}`}>
        <header className="messages-header">
          <div className="messages-header-text">
            <h1>Messages</h1>
            <p className="muted">
              {loading
                ? 'Loading…'
                : inboxConversations.length === 0
                  ? 'No conversations yet'
                  : totalUnread > 0
                    ? `${totalUnread} unread · ${inboxConversations.length} ${inboxConversations.length === 1 ? 'conversation' : 'conversations'}`
                    : `${inboxConversations.length} ${inboxConversations.length === 1 ? 'conversation' : 'conversations'}`}
            </p>
          </div>
          <button
            className="btn-primary messages-new-btn"
            onClick={() => setShowNewChat(true)}
            aria-label="New message"
            title="New message"
          >
            <Plus size={22} strokeWidth={2.5} />
          </button>
        </header>

        {showNewChat ? renderNewChatPanel() : renderConversationList()}
      </aside>

      {/* CENTER — active conversation */}
      <section className={`messages-conv-col${selectedConversation ? ' show-mobile' : ''}`}>
        {selectedConversation && activeDisplay ? (
          <div className="conv-shell card">
            {(() => {
              const HeaderTag = activeDisplay.isGroup ? 'button' : 'div'
              return (
                <HeaderTag
                  type={activeDisplay.isGroup ? 'button' : undefined}
                  className={`conv-header${activeDisplay.isGroup ? ' conv-header-clickable' : ''}`}
                  onClick={activeDisplay.isGroup ? openGroupInfo : undefined}
                  title={activeDisplay.isGroup ? 'Tap to rename or see members' : undefined}
                >
                  <button
                    type="button"
                    className="btn-ghost conv-back mobile-only"
                    onClick={(e) => { e.stopPropagation(); setSelectedConversation(null) }}
                    aria-label="Back"
                  >
                    <ArrowLeft size={18} />
                  </button>
                  {renderAvatarStack(activeDisplay)}
                  <div className="conv-header-text">
                    <span className="conv-header-name">{activeDisplay.name}</span>
                    <span className="muted conv-header-sub">
                      {activeDisplay.isGroup
                        ? `${activeDisplay.memberCount} members · tap to edit`
                        : `Last active ${formatTime(selectedConversation.lastMessageAt) || 'now'}`}
                    </span>
                  </div>
                </HeaderTag>
              )
            })()}

            <div className="conv-messages">
              {(() => {
                const isGroup = activeDisplay.isGroup
                return messages.map((msg, idx) => {
                  const isSent = msg.senderId === userProfile.uid
                  const showSender = !isSent && isGroup && (
                    idx === 0 || messages[idx - 1].senderId !== msg.senderId
                  )
                  return (
                    <div key={msg.id} className={`msg-row ${isSent ? 'sent' : 'received'}`}>
                      <div className="msg-bubble">
                        {showSender && (
                          <span className="msg-sender">
                            {msg.senderName ||
                              selectedConversation.participantNames?.[msg.senderId] ||
                              'Neighbor'}
                          </span>
                        )}
                        <p>{msg.text || msg.content}</p>
                        <span className="msg-time">{formatTime(msg.createdAt)}</span>
                      </div>
                    </div>
                  )
                })
              })()}
              <div ref={messagesEndRef} />
            </div>

            <form className="form-card conv-composer" onSubmit={sendMessage}>
              <textarea
                rows={1}
                placeholder="Type a message…"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    sendMessage(e)
                  }
                }}
              />
              <button
                type="submit"
                className="btn-primary conv-send"
                disabled={!newMessage.trim()}
                aria-label="Send"
              >
                <Send size={18} />
              </button>
            </form>
          </div>
        ) : (
          <div className="card conv-empty">
            <MessageCircle size={56} />
            <h3>Select a conversation</h3>
            <p className="muted">Choose a conversation from the list or start a new one.</p>
          </div>
        )}
      </section>

      {/* RIGHT — context / participants */}
      <aside className="detail-side messages-context">
        {selectedConversation && activeDisplay ? (
          activeDisplay.isGroup ? (
            <div className="card context-card">
              <div className="context-heading">Members</div>
              <p className="muted context-sub">
                {activeDisplay.memberCount} people in this conversation
              </p>
              <div className="context-members">
                {(selectedConversation.participants || []).map(uid => {
                  const name = selectedConversation.participantNames?.[uid] || 'Unknown'
                  const photo = selectedConversation.participantPhotos?.[uid]
                  const isMe = uid === userProfile.uid
                  return (
                    <div key={uid} className="context-member">
                      <div className="avatar sm" aria-hidden="true">
                        {photo
                          ? <img src={photo} alt="" />
                          : <span>{name?.charAt(0).toUpperCase()}</span>}
                      </div>
                      <span className="context-member-name">
                        {name}{isMe && <span className="muted"> (you)</span>}
                      </span>
                    </div>
                  )
                })}
              </div>
              <button type="button" className="btn-outline context-edit" onClick={openGroupInfo}>
                Rename group
              </button>
            </div>
          ) : (
            (() => {
              // Look up the DM partner's user doc for their address.
              // Falls back to the generic "Neighbor" label when the
              // address isn't populated or the partner isn't in the
              // loaded neighbors list yet.
              const partner = neighbors.find(n => n.id === activeDisplay.otherUserId)
              const addressLine = partner?.streetAddress || partner?.address || ''
              return (
                <div className="card context-card context-dm">
                  <div className="avatar xl" aria-hidden="true">
                    {activeDisplay.photo
                      ? <img src={activeDisplay.photo} alt="" />
                      : <span>{activeDisplay.name?.charAt(0).toUpperCase()}</span>}
                  </div>
                  <div className="context-dm-name">{activeDisplay.name}</div>
                  <p className="muted context-dm-sub">
                    {addressLine || 'Neighbor'}
                  </p>
                </div>
              )
            })()
          )
        ) : (
          <div className="card context-card context-tip">
            <div className="context-heading">Tip</div>
            <p className="muted">
              Message any neighbor. Group threads keep everyone in the loop.
            </p>
            <button
              type="button"
              className="btn-outline"
              onClick={() => setShowNewChat(true)}
            >
              <Plus size={16} /> New message
            </button>
          </div>
        )}
      </aside>

      {/* Group info modal — unchanged flow */}
      {showGroupInfo && selectedConversation && (
        <div className="modal-overlay" onClick={() => setShowGroupInfo(false)}>
          <div
            className="group-info-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Group info"
          >
            <div className="group-info-header">
              <h3>Group info</h3>
              <button
                type="button"
                className="btn-ghost group-info-close"
                onClick={() => setShowGroupInfo(false)}
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            <div className="group-info-body">
              <label className="group-info-label">Group name</label>
              <input
                type="text"
                value={groupInfoNameDraft}
                onChange={(e) => setGroupInfoNameDraft(e.target.value)}
                placeholder="(Auto-named from members)"
                maxLength={60}
                autoFocus
              />
              <p className="muted group-info-hint">
                Leave blank to use the default name (a list of member names).
              </p>
              <div className="group-info-members-header">
                <span>{selectedConversation.participants?.length || 0} members</span>
              </div>
              <div className="group-info-members-list">
                {(selectedConversation.participants || []).map(uid => {
                  const name = selectedConversation.participantNames?.[uid] || 'Unknown'
                  const photo = selectedConversation.participantPhotos?.[uid]
                  const isMe = uid === userProfile.uid
                  return (
                    <div key={uid} className="context-member">
                      <div className="avatar sm" aria-hidden="true">
                        {photo
                          ? <img src={photo} alt="" />
                          : <span>{name?.charAt(0).toUpperCase()}</span>}
                      </div>
                      <span className="context-member-name">
                        {name}{isMe && <span className="muted"> (you)</span>}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
            <div className="group-info-footer">
              <button type="button" className="btn-outline" onClick={() => setShowGroupInfo(false)} disabled={savingGroupName}>
                Cancel
              </button>
              <button type="button" className="btn-primary" onClick={saveGroupName} disabled={savingGroupName}>
                {savingGroupName ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MessagesPage
